import React, { useEffect, useState } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  Modal,
  Select,
  Spin,
  message,
} from "antd";
import style from "css/admin/component.module.css";
import SearchInput from "component/admin/search/searchInput";
import axios from "axios";

function ManualRegisterModal({ open, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [periodList, setPeriodList] = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [findLoading, setFindLoading] = useState(false);
  const [memberFound, setMemberFound] = useState(false);
  const [memberTypeOptions, setMemberTypeOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setMemberFound(false);
    setPeriodLoading(true);
    axios
      .get("/period", { params: {} })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setPeriodList(res.data);
        }
      })
      .catch(() => setPeriodList([]))
      .finally(() => setPeriodLoading(false));
    axios
      .get("/code", { params: { GROUP_CD: "CD01" } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setMemberTypeOptions(
            res.data.map((c) => ({
              value: c.CODE_CD,
              label: c.VIEW_NM,
            })),
          );
        }
      })
      .catch(() => setMemberTypeOptions([]));
  }, [open, form]);

  const closeHandler = () => {
    form.resetFields();
    setMemberFound(false);
    onCancel();
  };

  const handleFind = async () => {
    const memberId = form.getFieldValue("memberId");
    if (!memberId || !memberId.trim()) {
      message.warning("학번을 입력하세요.");
      return;
    }
    setFindLoading(true);
    try {
      const res = await axios.get("/user/find", {
        params: { MEMBER_ID: memberId.trim() },
      });
      if (res.status === 200 && res.data) {
        form.setFieldsValue({
          memberNm: res.data.MEMBER_NM || "",
          email: res.data.EMAIL || "",
          departmentNm: res.data.DEPARTMENT_NM || "",
          memberType: res.data.MEMBER_TYPE || undefined,
        });
        setMemberFound(true);
        message.success("조회 완료");
      } else {
        form.setFieldsValue({
          memberNm: "",
          email: "",
          departmentNm: "",
          memberType: undefined,
        });
        setMemberFound(false);
        message.warning("등록되지 않은 학번입니다. 정보를 직접 입력해주세요.");
      }
    } catch (e) {
      form.setFieldsValue({
        memberNm: "",
        email: "",
        departmentNm: "",
        memberType: undefined,
      });
      setMemberFound(false);
      message.error("조회 중 오류가 발생했습니다.");
    } finally {
      setFindLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const res = await axios.post("/application/manual", {
        MEMBER_ID: values.memberId.trim(),
        PERIOD_ID: values.periodId,
        MEMBER_NM: values.memberNm || "",
        EMAIL: values.email || "",
        DEPARTMENT_NM: values.departmentNm || "",
        MEMBER_TYPE: values.memberType || null,
      });

      if (res.status === 200 && res.data) {
        const d = res.data;
        if (d.status === "FAIL") {
          message.error(d.message || "등록에 실패했습니다.");
        } else {
          message.success(d.message || "등록이 완료되었습니다.");
          if (typeof onSuccess === "function") onSuccess(d);
        }
      }
    } catch (e) {
      if (e?.errorFields) {
        message.error("필수 항목을 확인해주세요.");
        return;
      }
      const msg =
        e?.response?.data ?? e?.message ?? "등록 중 오류가 발생했습니다.";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span className={style.searchItem_label} style={{ fontSize: 18 }}>
          사용자 수기 등록
        </span>
      }
      open={open}
      onCancel={closeHandler}
      closable={false}
      maskClosable={!loading}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div className={style.modal_footer_wrap}>
            <Button
              key="cancel"
              onClick={closeHandler}
              disabled={loading}
              className={style.modal_footer_btn}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              className={style.modal_footer_btn}
            >
              등록하기
            </Button>
          </div>
        </ConfigProvider>
      }
      rootClassName={style.manual_modal}
      styles={{ mask: { backdropFilter: "none" } }}
      width={480}
      centered
    >
      <Spin spinning={loading}>
        <Form form={form} requiredMark={false} style={{ marginTop: 10 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              padding: "23px 0",
            }}
          >
            <div className={style.form_row}>
              <div className={style.form_label}>학번</div>
              <Form.Item
                name="memberId"
                rules={[{ required: true, message: "학번을 입력하세요" }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <SearchInput
                  placeholder="학번을 입력하세요"
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFind();
                    }
                  }}
                  onChange={() => {
                    if (memberFound) setMemberFound(false);
                  }}
                />
              </Form.Item>
              <Button
                onClick={handleFind}
                loading={findLoading}
                style={{
                  height: 36,
                  borderRadius: 5,
                  fontSize: 14,
                  fontFamily: "Pretendard",
                  flexShrink: 0,
                }}
              >
                조회
              </Button>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>이름</div>
              <Form.Item name="memberNm" style={{ flex: 1, marginBottom: 0 }}>
                <SearchInput placeholder="" fullWidth disabled={memberFound} />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>이메일</div>
              <Form.Item name="email" style={{ flex: 1, marginBottom: 0 }}>
                <SearchInput placeholder="" fullWidth />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>학과</div>
              <Form.Item
                name="departmentNm"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <SearchInput placeholder="" fullWidth disabled={memberFound} />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>구분</div>
              <Form.Item name="memberType" style={{ flex: 1, marginBottom: 0 }}>
                <Select
                  className={style.search_cascader}
                  placeholder="선택하세요"
                  disabled={memberFound}
                  allowClear
                  options={memberTypeOptions}
                />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>신청기간 목록</div>
              <Form.Item
                name="periodId"
                rules={[{ required: true, message: "신청기간을 선택하세요" }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  className={style.search_cascader}
                  placeholder={
                    periodLoading ? "목록 불러오는 중..." : "선택하세요"
                  }
                  loading={periodLoading}
                  disabled={periodLoading}
                  options={(periodList || []).map((p) => ({
                    value: p.PERIOD_ID,
                    label: p.PERIOD_NM || p.PERIOD_ID,
                  }))}
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
}

export default ManualRegisterModal;
